import { BeneficiaryContextProvider } from 'context/beneficiaryContext';
import { useAuthContext } from 'context/authContext';
import { getRelativePath } from 'helpers/utils';
import { useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Navigate, Route, Routes } from 'react-router-dom';
import paths from 'routes/paths';
import {
  permissionsKeyMap,
  userActions
} from 'routes/permissions/permissionKeys';
import BeneficiaryNav from './BeneficiaryNav';
import BeneficiaryProfile from './BeneficiaryProfile';
import Donors from './donor';
import EditDonor from './donor/EditDonor';
import Gellery from './gallery';
import Mosque from './mosque';
import School from './school';
import AddSchool from './school/AddSchool';
import CreateSchoolReportPage from './school/CreateSchoolReportPage';
import CreateSponsorship from './sponsorships/CreateSponsorship';
import Hospital from './hospital';
import Orphanage from './orphanage';
import CardNav from './CardNav';
import ProfileCard from './ProfileCard';
import BeneficiaryFeedback from './notes/BeneficiaryFeedback';
import BeneficiarySupportingDocs from './supporting-docs/BeneficiarySupportingDocs';
import BeneficiaryCaseStudies from './case-studies/BeneficiaryCaseStudies';

const BeneficiaryLayoutContent = () => {
  const { ability } = useAuthContext();
  const parentRef = useRef(null);
  const navRef = useRef(null);
  const [isWidthReady, setIsWidthReady] = useState(false);
  const [isHeightReady, setIsHeightReady] = useState(false);

  const [parentWidth, setParentWidth] = useState(0);
  const [navHeight, setNavHeight] = useState(0);

  // Check if user has permission to view donors
  const canViewDonors = ability.can(
    userActions.read,
    permissionsKeyMap.donor.children.donors
  );

  useEffect(() => {
    if (!isWidthReady) return;
    // Re-measure on resize/rotation: the nav is position-fixed with an
    // explicit width, and its height changes when the tabs wrap on phones
    const measure = () => {
      setParentWidth(parentRef?.current?.clientWidth);
      setNavHeight(navRef?.current?.clientHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isWidthReady, isHeightReady]);

  return (
    <div
      className="position-relative mb-5"
      style={{
        padding: '0px !important'
      }}
      ref={element => {
        parentRef.current = element;
        setIsWidthReady(true);
      }}
    >
      {parentWidth ? (
        <div
          id="top-bar"
          className="position-fixed"
          ref={element => {
            navRef.current = element;
            setIsHeightReady(true);
          }}
          style={{
            zIndex: 2,
            width: parentWidth
          }}
        >
          <BeneficiaryNav />
        </div>
      ) : null}

      <div
        style={{
          paddingTop: navHeight ?? 0
        }}
      >
        <Row>
          <Col lg={3} xs={12}>
            <ProfileCard />
            <CardNav />
          </Col>

          <Col lg={9} xs={12}>
            <Routes>
              <Route
                path={getRelativePath(
                  paths.beneficiaryProfile,
                  paths.beneficiaryDetails
                )}
                element={<BeneficiaryProfile />}
              />

              {canViewDonors && (
                <Route
                  path={getRelativePath(
                    paths.beneficiaryCreateNewSponsorships,
                    paths.beneficiaryDetails
                  )}
                  element={<CreateSponsorship />}
                />
              )}
              {canViewDonors && (
                <>
                  <Route
                    path={getRelativePath(
                      paths.beneficiaryDonors,
                      paths.beneficiaryDetails
                    )}
                    element={<Donors />}
                  />
                  <Route
                    path={getRelativePath(
                      paths.beneficiaryDonorEdit,
                      paths.beneficiaryDetails
                    )}
                    element={<EditDonor />}
                  />
                </>
              )}

              <Route
                path={getRelativePath(
                  paths.beneficiaryShool,
                  paths.beneficiaryDetails
                )}
                element={<School />}
              />
              <Route
                path={getRelativePath(
                  paths.beneficiaryMosque,
                  paths.beneficiaryDetails
                )}
                element={<Mosque />}
              />
              <Route
                path={getRelativePath(
                  paths.beneficiaryHospital,
                  paths.beneficiaryDetails
                )}
                element={<Hospital />}
              />
              <Route
                path={getRelativePath(
                  paths.beneficiaryOrphanage,
                  paths.beneficiaryDetails
                )}
                element={<Orphanage />}
              />
              <Route
                path={getRelativePath(
                  paths.beneficiaryAddSchool,
                  paths.beneficiaryDetails
                )}
                element={<AddSchool />}
              />
              <Route
                path={getRelativePath(
                  paths.beneficiaryCreateSchoolReport,
                  paths.beneficiaryDetails
                )}
                element={<CreateSchoolReportPage />}
              />

              <Route
                path={getRelativePath(
                  paths.beneficiaryGallery,
                  paths.beneficiaryDetails
                )}
                element={<Gellery />}
              />

              <Route
                path={getRelativePath(
                  paths.beneficiaryNotes,
                  paths.beneficiaryDetails
                )}
                element={<BeneficiaryFeedback />}
              />

              <Route
                path={getRelativePath(
                  paths.beneficiarySupportingDocs,
                  paths.beneficiaryDetails
                )}
                element={<BeneficiarySupportingDocs />}
              />

              <Route
                path={getRelativePath(
                  paths.beneficiaryCaseStudies,
                  paths.beneficiaryDetails
                )}
                element={<BeneficiaryCaseStudies />}
              />

              <Route
                path="*"
                element={
                  <Navigate
                    to={getRelativePath(
                      paths.beneficiaryProfile,
                      paths.beneficiaryDetails
                    )}
                    replace
                  />
                }
              />
            </Routes>
          </Col>
        </Row>
      </div>
    </div>
  );
};

const BeneficiaryLayout = () => {
  return (
    <BeneficiaryContextProvider>
      <BeneficiaryLayoutContent />
    </BeneficiaryContextProvider>
  );
};

export default BeneficiaryLayout;
